import type { Maybe } from "purify-ts";
import type { GetArrayElementType } from "widgets/utils";
import type z from "zod";

import type { WorkflowActivityType } from "@/graphql.ts";
import {
  type InputValueWrapperInput,
  JobStatusEnum,
  type NumberControlledValue,
  type StringControlledValue,
  type SupportedExecutorsEnum,
  type TrackerEnum,
  type WorkflowActivityStatusEnum,
} from "@/graphql.ts";
import type {
  ExecuteWorkflowMutation,
  GetActivitiesQuery,
  GetSampleTypeQuery,
  GetVisibleWorkflowEventsQuery,
  GetWorkflowEventDetailsQuery,
  GetWorkflowsQuery,
  LoadWorkflowEventQuery,
  LoadWorkflowPreviewDetailsQuery,
  LoadWorkflowQuery,
  PublishWorkflowMutation,
  ResetWorkflowMutation,
} from "@/modules/workflow/graphql/workflow.gql.ts";

// --- Activity --- //

type ActivityWithoutVersions = NonNullable<
  GetArrayElementType<NonNullable<NonNullable<GetActivitiesQuery["workflow"]>["activityCollector"]["activities"]>>
>;

export type Activity = ActivityWithoutVersions & {
  versions?: (ActivityWithoutVersions["version"] & { default: ActivityWithoutVersions["default"] })[];
};

export type ActivityTag = GetArrayElementType<NonNullable<Activity["tags"]>>;
export type ActivityInputOutput = GetArrayElementType<NonNullable<Activity["inputs"]>>;

export type ActivityParameter = GetArrayElementType<NonNullable<ActivityWithoutVersions["parameters"]>>;
export type ActivityControlledValue = NumberControlledValue | StringControlledValue;

export type ActivityErrors = NonNullable<
  GetArrayElementType<NonNullable<NonNullable<GetActivitiesQuery["workflow"]>["activityCollector"]["errors"]>>
>;

export type NodeInstance = GetArrayElementType<
  ResetWorkflowMutation["workflow"]["resetWorkflow"]["config"]["activities"]
>;

// --- Nextflow --- //

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type EditorNodeData = {
  activityName: string;
  version: string;
  status?: WorkflowActivityStatusEnum;
  computing?: SupportedExecutorsEnum;
  outputFolderId?: number;
  disabled?: boolean;
  activityType?: WorkflowActivityType;
  // ...
};

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type PreviewNodeData = {
  activity: Activity | undefined; // might be undefined when not found
  status?: WorkflowActivityStatusEnum;
  width: number;
  skipped?: boolean;
  staged?: boolean;
  disabled?: boolean;
  // ...
};

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type EdgeData = {
  label: string;
};

// --- Selector Workflow --- //

export type DndNodeData = Pick<EditorNodeData, "version" | "activityName">;

export interface SelectedNodeActivity {
  nodeId?: string; // if undefined, not a node
  activityName: string; // needed if not a node, although info is in mode too
  version: string;
  /** Nextflow Activity. Allows to edit parameters in the Inspection Panel */
}

export type AllParametersTypes = InputValueWrapperInput[keyof InputValueWrapperInput];

interface WorkflowParameterBase {
  nodeId: string;
  parameterId: string;
  values: Maybe<AllParametersTypes>;
  staged: boolean;
  multiSelect: boolean;
  schema?: z.ZodType<AllParametersTypes>;
}

interface CommonWorkflowParameter extends WorkflowParameterBase {
  typeName: Exclude<ActivityParameter["__typename"], "TrackerParam">;
  tracker: null;
}

interface TrackerWorkflowParameter extends WorkflowParameterBase {
  typeName: Extract<ActivityParameter["__typename"], "TrackerParam">;
  tracker: TrackerEnum;
}

export type WorkflowParameter = CommonWorkflowParameter | TrackerWorkflowParameter;

// Mapping between Activity types and Workflow types
export type StringParamType = NonNullable<InputValueWrapperInput["textValue"]>;
export type NumberParamType = NonNullable<InputValueWrapperInput["numberValue"]>;
export type BooleanParamType = NonNullable<InputValueWrapperInput["booleanValue"]>;
export type DateParamType = NonNullable<InputValueWrapperInput["dateValue"]>; // Not important
export type FileParamType = NonNullable<InputValueWrapperInput["fileId"]>;
export type SampleParamType = NonNullable<InputValueWrapperInput["sampleValue"]>;
export type OrganismParamType = NonNullable<InputValueWrapperInput["organismId"]>;
export type RegionParamType = NonNullable<InputValueWrapperInput["region"]>;
export type TrackerParamType = NonNullable<InputValueWrapperInput["attributeId"]>;
export type TableParamType = NonNullable<InputValueWrapperInput["viewId"]>;

export type WorkflowActivityParameterFieldProps = ActivityParameter & {
  nodeId: string;
};

export enum ZodCustomErrorParameterCode {
  Unavailable = "unavailable_content",
  Duplicate = "duplicate_content",
}

export interface WorkflowErrorParameter {
  nodeId: string;
  parameterId: string;
  message: string;
  type: "parameter" | "tracker" | "sample";
  /** For identifying formally specific errors e.g. for blocking the saving when restricted content is detected */
  code?: ZodCustomErrorParameterCode;
}

export interface WorkflowErrorConnector {
  nodeId: string;
  targetHandle: string;
  message: string;
  type: "connection";
}

// --- Other --- //

export type WorkflowExecutionData = NonNullable<NonNullable<ExecuteWorkflowMutation["workflow"]>["executeWorkflow"]>;
export type WorkflowPublishData = NonNullable<NonNullable<PublishWorkflowMutation["workflow"]>["publishWorkflow"]>;
export type WorkflowLoadData = NonNullable<NonNullable<LoadWorkflowQuery["workflow"]>["findWorkflowById"]>;
export type WorkflowEventLoadData = NonNullable<
  NonNullable<LoadWorkflowEventQuery["workflow"]>["findWorkflowEventById"]
>;
export type WorkflowEditorNodeData = GetArrayElementType<
  NonNullable<NonNullable<WorkflowLoadData["config"]>["activities"]>
>;

export type WorkflowListData = NonNullable<
  GetArrayElementType<NonNullable<NonNullable<GetWorkflowsQuery["workflow"]>["listWorkflows"]>>
>;
export type SessionListData = NonNullable<
  GetArrayElementType<NonNullable<NonNullable<GetVisibleWorkflowEventsQuery["workflow"]>["listWorkflowEvents"]>>
>;

export type SessionData = NonNullable<
  NonNullable<NonNullable<GetWorkflowEventDetailsQuery["workflow"]>["findWorkflowEventById"]>
>;

export type WorkflowPreviewDetailsData = NonNullable<LoadWorkflowPreviewDetailsQuery["workflow"]>["findWorkflowById"];

export type WorkflowSampleAttributes = NonNullable<
  NonNullable<NonNullable<GetSampleTypeQuery["sample"]>["sampleType"]>["sampleAttributes"]
>;

type _ExtractActivityType<Type extends ActivityParameter, TypeName extends string> = Type extends {
  __typename: TypeName;
}
  ? Type
  : never;

type ExtractActivityType<TypeName extends string> = _ExtractActivityType<ActivityParameter, TypeName>;

export type TrackerActivity = ExtractActivityType<"TrackerParam">;

// --- Copy/Paste/Cut --- //

export const LOCAL_STORAGE_KEY = "workflow_editor_copy_data";

// --- Enums not in GraphQL --- //

export enum ExtendedJobStatusEnum {
  Stopping = "STOPPING",
  Loading = "Loading", // Is Initializing, but local
  Ready = "Ready", // Use it only if it never ran before. Default value.
  Completed = JobStatusEnum.Completed,
  Error = JobStatusEnum.Error,
  Initializing = JobStatusEnum.Initializing,
  Queued = JobStatusEnum.Queued,
  Running = JobStatusEnum.Running,
  Terminated = JobStatusEnum.Terminated,
}

// --- Settings --- //

export enum InspectionTabs {
  Workflow = "workflow",
  Activity = "activity",
  Parameters = "parameters",
  Tracker = "tracker",
  Problems = "problems",
}

export enum StatusBarTabs {
  Files = "files",
  Logs = "logs",
  Issues = "issues",
  Publish = "publish",
}

export enum SELECTABLE_WORKFLOW_TYPE {
  PUBLISHED = "Published",
  TEMPLATE = "Template",
}
